// Competitor filters (PD-17066). The server applies every one of these in
// CompetitorsService (joinType 'custom'): most are EXISTS conditions over the
// competitor's customer links, which the generic filter builder cannot express.

import {
	COMPETITOR_RELATIONSHIP_LABELS,
	COMPETITOR_RELATIONSHIPS,
} from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createKeywordFilter,
	createRadioFilter,
} from './factories';
import type { FilterConfig, FilterRegistry } from './types';

const COMPETITOR_LIST_KEYS = ['competitor'];

const RELATIONSHIP_OPTIONS = COMPETITOR_RELATIONSHIPS.map((relationship) => ({
	id: relationship,
	label: COMPETITOR_RELATIONSHIP_LABELS[relationship],
}));

function customRadio(
	label: string,
	options: Array<{ id: string; label: string }>,
): FilterConfig {
	return {
		...createRadioFilter({
			label,
			options,
			objectTable: 'competitor',
			fieldName: '',
			listKeys: COMPETITOR_LIST_KEYS,
		}),
		queryMapping: { objectTable: 'competitor', joinType: 'custom' },
	};
}

function numberRange(label: string, suffix?: string): FilterConfig {
	return {
		type: 'number-range',
		label,
		placeholder: 'Any',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: '',
		multiple: false,
		queryMapping: { objectTable: 'competitor', joinType: 'custom' },
		format: 'single',
		valueExtractor: {
			property: 'data',
		},
		min: 0,
		step: 1,
		...(suffix != null ? { suffix } : {}),
		listKeys: COMPETITOR_LIST_KEYS,
	};
}

export const competitorFilters: FilterRegistry = {
	competitorKeywords: {
		...createKeywordFilter({
			label: 'Keywords',
			placeholder: 'Search name, other names, website, phone, city, notes...',
			objectTable: 'competitor',
			listKeys: COMPETITOR_LIST_KEYS,
		}),
		queryParamName: 'competitorKeywords',
	},

	competitorStatus: {
		...customRadio('Status', [
			{ id: 'active', label: 'Active' },
			{ id: 'inactive', label: 'Inactive' },
		]),
		queryParamName: 'competitorStatus',
	},

	competitorRelationship: {
		type: 'multi-select',
		label: 'Relationship',
		placeholder: 'Select relationships...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'competitorRelationship',
		multiple: true,
		options: RELATIONSHIP_OPTIONS,
		queryMapping: { objectTable: 'competitor', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: COMPETITOR_LIST_KEYS,
	},

	// Product groups are the item `category` values (the sales reports' product group).
	competitorProductGroups: {
		type: 'multi-select',
		label: 'Product Group',
		placeholder: 'Search product groups...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'category' as keyof unknown,
			primary: 'category' as keyof unknown,
		},
		apiEndpoint: '/items/categories',
		queryParamName: 'competitorProductGroups',
		multiple: true,
		queryMapping: { objectTable: 'competitor', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: COMPETITOR_LIST_KEYS,
	},

	// The salesperson (or alternate) of a customer linked to the competitor.
	// The "My accounts" tab locks this to the caller on the server.
	competitorSalespeople: {
		type: 'multi-select',
		label: "Customer's Salesperson",
		placeholder: 'Search salespeople...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		queryParamName: 'competitorSalespeople',
		multiple: true,
		options: [
			{ id: 'ME', label: 'Me' },
			{ id: 'ME_ALTERNATE', label: 'Me (Alternate)' },
			{ id: 'null', label: 'Unassigned' },
		],
		queryMapping: { objectTable: 'competitor', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: COMPETITOR_LIST_KEYS,
	},

	competitorTeams: {
		type: 'multi-select',
		label: "Customer's Team",
		placeholder: 'Search teams...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'team_id' as keyof unknown,
			primary: 'team_name' as keyof unknown,
		},
		apiEndpoint: '/teams',
		queryParamName: 'competitorTeams',
		multiple: true,
		queryMapping: { objectTable: 'competitor', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: COMPETITOR_LIST_KEYS,
	},

	competitorCustomerStates: {
		type: 'multi-select',
		label: "Customer's State",
		placeholder: 'Select state...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/clients/state/options',
		queryParamName: 'competitorCustomerStates',
		multiple: true,
		queryMapping: { objectTable: 'competitor', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: COMPETITOR_LIST_KEYS,
	},

	competitorHasPrices: {
		...customRadio('Has Competitor Prices', [
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		]),
		queryParamName: 'competitorHasPrices',
	},

	competitorAlsoVendor: {
		...customRadio('Also Our Vendor', [
			{ id: 'yes', label: 'Yes' },
			{ id: 'no', label: 'No' },
		]),
		queryParamName: 'competitorAlsoVendor',
	},

	competitorCustomerCount: {
		...numberRange('Customer Count'),
		queryParamName: 'competitorCustomerCount',
	},

	competitorEstimatedSpend: {
		...numberRange('Estimated Annual Spend'),
		queryParamName: 'competitorEstimatedSpend',
	},

	competitorLastConfirmed: {
		...customRadio('Last Confirmed', [
			{ id: 'older_6', label: 'Over 6 months ago or never' },
			{ id: 'older_12', label: 'Over 12 months ago or never' },
			{ id: 'never', label: 'Never' },
		]),
		queryParamName: 'competitorLastConfirmed',
	},

	competitorAddedDate: {
		...createDateRangeFilter({
			label: 'Added',
			objectTable: 'competitor',
			dateField: '_created_date',
			listKeys: COMPETITOR_LIST_KEYS,
		}),
		queryMapping: {
			objectTable: 'competitor',
			dateField: '_created_date',
			joinType: 'custom',
		},
		queryParamName: 'competitorAddedDate',
	},

	// The "Recently added" tab's predicate (added in the last N days).
	competitorRecentlyAdded: {
		...customRadio('Recently Added', [
			{ id: '30', label: 'Last 30 days' },
			{ id: '90', label: 'Last 90 days' },
		]),
		queryParamName: 'competitorRecentlyAdded',
	},

	// PD-13446 — the Competitive Pricing report's Competitor filter: prices
	// linked to the picked competitors. Inactive competitors are offered too,
	// since their old prices still show in the report.
	competitors: {
		type: 'multi-select',
		label: 'Competitor',
		placeholder: 'Search competitors...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/competitors/options',
		apiParams: { includeInactive: 'true' },
		queryParamName: 'competitors',
		multiple: true,
		queryMapping: {
			objectTable: 'competitive_price',
			fieldName: 'fk_wholesaler__id',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['reports'],
	},

	// A competitor's Customers tab: its sub-tabs (Primary, Secondary, ...).
	competitorLinkRelationship: {
		type: 'multi-select',
		label: 'Relationship',
		placeholder: 'Select relationships...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'competitorLinkRelationship',
		multiple: true,
		options: RELATIONSHIP_OPTIONS,
		queryMapping: { objectTable: 'competitor_link', joinType: 'custom' },
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['competitor-customers-embedded'],
	},
};
